import api, { setAccessToken as setGlobalAccessToken } from './api';
import logger from '../utils/logger';

const getApiErrorMessage = (error) => {
  const responseData = error.response?.data;

  if (typeof responseData === 'string') {
    return responseData;
  }

  const fieldErrors = responseData?.errors;
  if (fieldErrors && typeof fieldErrors === 'object') {
    const firstFieldError = Object.values(fieldErrors).find(Boolean);
    if (firstFieldError) {
      return firstFieldError;
    }
  }

  return responseData?.message || responseData?.error || error.message;
};

const isFormDataPayload = (data) =>
  typeof FormData !== 'undefined' && data instanceof FormData;

const normalizeApiError = (error) => {
  const requestError = new Error(getApiErrorMessage(error));
  requestError.status = error.response?.status;
  requestError.response = error.response;
  return requestError;
};

const withCompanyQuery = (endpoint, companyId) => {
  if (!companyId) return endpoint;
  const params = new URLSearchParams({ company_id: companyId });
  return endpoint + '?' + params.toString();
};

class InvoicingService {
  constructor() {
    this.endpoints = {
      customers: '/api/invoicing/customers',
      invoices: '/api/invoicing/invoices',
      proposals: '/api/invoicing/proposals',
      companyBranding: (companyId) =>
        `/api/invoicing/companies/${companyId}/branding`,
    };
  }

  setAccessToken(token) {
    setGlobalAccessToken(token);
  }

  async request(endpoint, method = 'GET', data = undefined, headers = undefined) {
    const config = { method };
    if (headers) {
      config.headers = headers;
    }
    if (data !== undefined) {
      config.data = data;
    }

    if (isFormDataPayload(data)) {
      config.headers = { ...config.headers, 'Content-Type': undefined };
    }

    try {
      const response = await api(endpoint, config);
      return response.data;
    } catch (error) {
      throw normalizeApiError(error);
    }
  }

  async listCustomerRecords(companyId = null) {
    try {
      const result = await this.request(
        withCompanyQuery(this.endpoints.customers, companyId)
      );
      return result.data || [];
    } catch (error) {
      logger.error('Failed to list customer records:', error);
      throw error;
    }
  }

  async createCustomerRecord(payload) {
    try {
      const result = await this.request(
        this.endpoints.customers,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to create customer record:', error);
      throw error;
    }
  }

  async updateCustomerRecord(customerId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.customers}/${customerId}`,
        'PUT',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to update customer record:', error);
      throw error;
    }
  }

  async listInvoices(companyId = null) {
    try {
      const result = await this.request(
        withCompanyQuery(this.endpoints.invoices, companyId)
      );
      return result.data || [];
    } catch (error) {
      logger.error('Failed to list invoices:', error);
      throw error;
    }
  }

  async createInvoice(payload) {
    try {
      const result = await this.request(
        this.endpoints.invoices,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to create invoice:', error);
      throw error;
    }
  }

  async updateInvoice(invoiceId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.invoices}/${invoiceId}`,
        'PUT',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to update invoice:', error);
      throw error;
    }
  }

  async getInvoiceReconciliationSuggestions(invoiceId) {
    try {
      const result = await this.request(
        `${this.endpoints.invoices}/${invoiceId}/reconciliation-suggestions`
      );
      return result.data || [];
    } catch (error) {
      logger.error('Failed to load invoice reconciliation suggestions:', error);
      throw error;
    }
  }

  async confirmInvoiceReconciliation(
    invoiceId,
    transactionId,
    idempotencyKey
  ) {
    try {
      const result = await this.request(
        `${this.endpoints.invoices}/${invoiceId}/reconciliation-confirmations`,
        'POST',
        { transactionId },
        { 'Idempotency-Key': idempotencyKey }
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to confirm invoice reconciliation:', error);
      throw error;
    }
  }

  async getCompanyInvoiceBranding(companyId) {
    try {
      const result = await this.request(
        this.endpoints.companyBranding(companyId)
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to get company invoice branding:', error);
      throw error;
    }
  }

  async updateCompanyInvoiceBranding(companyId, payload) {
    try {
      const result = await this.request(
        this.endpoints.companyBranding(companyId),
        'PUT',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to update company invoice branding:', error);
      throw error;
    }
  }

  async uploadCompanyInvoiceLogo(companyId, file) {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const result = await this.request(
        `${this.endpoints.companyBranding(companyId)}/logo`,
        'POST',
        formData
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to upload company invoice logo:', error);
      throw error;
    }
  }

  async deleteCompanyInvoiceLogo(companyId) {
    try {
      const result = await this.request(
        `${this.endpoints.companyBranding(companyId)}/logo`,
        'DELETE'
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to delete company invoice logo:', error);
      throw error;
    }
  }

  async sendInvoice(invoiceId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.invoices}/${invoiceId}/send`,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to send invoice:', error);
      throw error;
    }
  }

  async requestInvoiceSendApproval(invoiceId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.invoices}/${invoiceId}/send-approval`,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to request invoice send approval:', error);
      throw error;
    }
  }

  async listProposals(companyId = null) {
    try {
      const result = await this.request(
        withCompanyQuery(this.endpoints.proposals, companyId)
      );
      return result.data || [];
    } catch (error) {
      logger.error('Failed to list proposals:', error);
      throw error;
    }
  }

  async createProposal(payload) {
    try {
      const result = await this.request(
        this.endpoints.proposals,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to create proposal:', error);
      throw error;
    }
  }

  async updateProposal(proposalId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.proposals}/${proposalId}`,
        'PUT',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to update proposal:', error);
      throw error;
    }
  }
  async convertProposalToInvoice(proposalId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.proposals}/${proposalId}/convert-to-invoice`,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to convert proposal to invoice:', error);
      throw error;
    }
  }

  async sendProposal(proposalId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.proposals}/${proposalId}/send`,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to send proposal:', error);
      throw error;
    }
  }

  async requestProposalSendApproval(proposalId, payload) {
    try {
      const result = await this.request(
        `${this.endpoints.proposals}/${proposalId}/send-approval`,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to request proposal send approval:', error);
      throw error;
    }
  }

  async getPublicProposal(token) {
    try {
      const result = await this.request(
        `/api/public/proposals/${encodeURIComponent(token)}`
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to load public proposal:', error);
      throw error;
    }
  }

  async createProposalAcceptanceLink(proposalId) {
    try {
      const result = await this.request(
        `${this.endpoints.proposals}/${proposalId}/acceptance-link`,
        'POST'
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to create proposal acceptance link:', error);
      throw error;
    }
  }

  async acceptPublicProposal(token, payload) {
    try {
      const result = await this.request(
        `/api/public/proposals/${encodeURIComponent(token)}/accept`,
        'POST',
        payload
      );
      return result.data || result;
    } catch (error) {
      logger.error('Failed to accept public proposal:', error);
      throw error;
    }
  }
}

const invoicingService = new InvoicingService();

export default invoicingService;
export { InvoicingService };
